import Peer from "simple-peer";
import store from "../stories/store";
import { getWidthComponent } from "../utilities/methods";
import {
    setAuthToken, setMessages, setNotifyMessage, setIsShowOverlay, setAudioSrc, setVolumeIconStream,
    setIsTimeStamp, setInitialMinutes, setInitialSeconds, setTasks, setSendQuestions, setDataNotes,
    setSendDataNotes, setStateDataNotes, setRemoveStateDataNotes, setRemoveDataNotes
} from "../stories/actions";
import { peerConfiguration } from "./peerConfiguration";
import { fetchTurnCredentials } from "./turn";
import * as roomWSS from "./roomWSS";

const AUTH_TOKEN_KEY = "authToken";
const ROOM_ID = "roomId";

const USERNAME_KEY = "username";
const CONTENT_KEY = "content";
const CREATE_BY_ME_KEY = "createdByMe";

const NOTIFY_SOCKET_ID = "socketId";
const NOTIFY_AVALUATOR = "avaluator";
const NOTIFY_ACTOR = "actor";

const MINUTES_KEY = "minutes";
const SECONDS_KEY = "seconds";

const IS_TIME_STAMP = "isTimeStamp";
const AUDIO_SRC = "audioSrc";

const ROOM_SOCKET_ID = "roomSocketId";
const IS_ONLY_AUDIO = "isOnlyAudio";

const TASK_ID = "id";
const AREA_ID = "areaId";
const TASK_ESTATION_ID = "estacaoId";
const TASK_SCENARIO = "cenario";
const AREA_NAME = "areaName";

const QUESTION_REALIZED = "perguntaRealizada";
const NOTE_A = "nota_A";
const NOTE_PA = "nota_PA";
const NOTE_I = "nota_I";

const PROGRESS_NOTE_ID = "id";
const PROGRESS_NOTE_CARD_ID = "cardId";
const PROGRESS_NOTE_VALUE = "note";
const PROGRESS_NOTE_SIZE = "progress";

const SEND_NOTE_ID = "idSendNote";
const SEND_NOTE_PARAGRAPH = "paragraph";
const SEND_NOTE_VALUE = "noteValue";
const SEND_NOTE_TYPE = "noteType";

const fallbackConstraints = {
    video: true,
    audio: true,
};

let audioContext;
let analyser;
let volumeThreshold = 0.1;
let animationFrameId = null;

let peers = {};
let streams = [];
let localStream;

const messengerChannel = "messenger";

export const getInitialUserConnection = async (isUserHost, isAdmin, username, sessionId = null) => {
    await fetchTurnCredentials();

    store.dispatch(setIsShowOverlay(false));

    !isUserHost && roomWSS.joinSession(isAdmin, username, sessionId);
};

export const getInitialRoomConnection = async (isRoomHost, identity, usuarioId, roomId, isOnlyAudio, isOnlyCamera, isRoomListener) => {
    await fetchTurnCredentials();

    try {
        const stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
        localStream = stream;

        if (localStream) {
            if (localStream.getAudioTracks().length > 0) {
                localStream.getAudioTracks()[0].enabled = isAudioController(isOnlyAudio);
            }

            if (localStream.getVideoTracks().length > 0) {
                localStream.getVideoTracks()[0].enabled = isCameraController(isOnlyCamera);
            }

            if (!isRoomListener) {
                showLocalVideoPreview(localStream, identity);
            }
        }
    } catch (error) {
        console.warn("Não foi possível capturar mídia local:", error);
        localStream = new MediaStream();
    }

    store.dispatch(setIsShowOverlay(false));

    isRoomHost ?
        roomWSS.createRoom(identity, usuarioId, isOnlyAudio, isOnlyCamera, isRoomListener) :
        roomWSS.joinRoom(roomId, identity, usuarioId, isOnlyAudio, isOnlyCamera, isRoomListener);
};

export const prepareRoomPeerConnection = (roomConnectedInSocketId, isInitiator) => {
    const configuration = peerConfiguration();

    peers[roomConnectedInSocketId] = new Peer({
        initiator: isInitiator,
        config: configuration,
        stream: localStream,
        channelName: messengerChannel,
        trickle: false
    });

    peers[roomConnectedInSocketId].on("connect", () => {
        console.log("DataChannel conectado");
    });

    peers[roomConnectedInSocketId].on('error', (error) => {
        if (error.message.includes('User-Initiated Abort')) {
            console.error("Erro no peer", error);
        }
    });

    peers[roomConnectedInSocketId].on("signal", (data) => {
        const signalData = {
            signal: data,
            roomConnectedInSocketId: roomConnectedInSocketId,
        };

        roomWSS.roomSignalPeerData(signalData);
    });

    peers[roomConnectedInSocketId].on("stream", (stream) => {
        streams = [...streams, stream];

        addStream(stream, roomConnectedInSocketId);
    });

    peers[roomConnectedInSocketId].on("data", (data) => {
        const resultData = JSON.parse(data);

        if (isAuthToken(resultData)) {
            serverDisconnect(resultData);
        } else if (isChatMessage(resultData)) {
            appendMessage(resultData);
        } else if (isNotifyMessage(resultData)) {
            appendNotifyMessage(resultData);
        } else if (isTimeStampData(resultData)) {
            appendTimeStamp(resultData);
        } else if (isTimeAlertData(resultData)) {
            appendTimeAlert(resultData);
        } else if (isVolumeIconStreamData(resultData)) {
            appendVolumeIconStream(resultData);
        } else if (isTaskData(resultData)) {
            appendTask(resultData);
        } else if (isSendQuestionData(resultData)) {
            appendSendQuestions(resultData);
        } else if (isProgressNoteData(resultData)) {
            if (resultData.action === "add") {
                store.dispatch(setRemoveDataNotes(resultData.id));
                store.dispatch(setRemoveStateDataNotes(resultData.id));

                appendProgressNote(resultData);
                appendSendStateDataNotes(resultData);
            } else if (resultData.action === "remove") {
                store.dispatch(setRemoveDataNotes(resultData.id));
                store.dispatch(setRemoveStateDataNotes(resultData.id));
            }
        } else if (isSendNoteData(resultData)) {
            appendSendNotes(resultData);
        }
    });
};

const isAuthToken = (data) => {
    return data.hasOwnProperty(AUTH_TOKEN_KEY) && data.hasOwnProperty(USERNAME_KEY);
};

const isChatMessage = (data) => {
    return data.hasOwnProperty(USERNAME_KEY) && data.hasOwnProperty(CONTENT_KEY) && data.hasOwnProperty(CREATE_BY_ME_KEY);
};

const isNotifyMessage = (data) => {
    return data.hasOwnProperty(NOTIFY_SOCKET_ID) && data.hasOwnProperty(NOTIFY_AVALUATOR) && data.hasOwnProperty(NOTIFY_ACTOR);
};

const isTimeStampData = (data) => {
    return data.hasOwnProperty(MINUTES_KEY) && data.hasOwnProperty(SECONDS_KEY) && data.hasOwnProperty(ROOM_ID);
};

const isTimeAlertData = (data) => {
    return data.hasOwnProperty(IS_TIME_STAMP) && data.hasOwnProperty(AUDIO_SRC) && data.hasOwnProperty(ROOM_ID);
};

const isVolumeIconStreamData = (data) => {
    return data.hasOwnProperty(ROOM_SOCKET_ID) && data.hasOwnProperty(IS_ONLY_AUDIO) && data.hasOwnProperty(ROOM_ID);
};

const isTaskData = (data) => {
    return data.hasOwnProperty(TASK_ID) && data.hasOwnProperty(TASK_ESTATION_ID) &&
        data.hasOwnProperty(TASK_SCENARIO) && data.hasOwnProperty(AREA_NAME) && data.hasOwnProperty(ROOM_ID);
};

const isSendQuestionData = (data) => {
    return data.hasOwnProperty(QUESTION_REALIZED) && data.hasOwnProperty(NOTE_A) &&
        data.hasOwnProperty(NOTE_PA) && data.hasOwnProperty(NOTE_I) && data.hasOwnProperty(ROOM_ID);
};

const isProgressNoteData = (data) => {
    return data.hasOwnProperty(PROGRESS_NOTE_ID) && data.hasOwnProperty(PROGRESS_NOTE_CARD_ID) &&
        data.hasOwnProperty(PROGRESS_NOTE_VALUE) && data.hasOwnProperty(PROGRESS_NOTE_SIZE) && data.hasOwnProperty(ROOM_ID);
};

const isSendNoteData = (data) => {
    return data.hasOwnProperty(SEND_NOTE_ID) && data.hasOwnProperty(SEND_NOTE_PARAGRAPH) &&
        data.hasOwnProperty(SEND_NOTE_VALUE) && data.hasOwnProperty(SEND_NOTE_TYPE) && data.hasOwnProperty(ROOM_ID);
};

export const handlerRoomSignalData = (data) => {
    const { roomConnectedInSocketId, signal } = data;
    const peer = peers[roomConnectedInSocketId];

    if (!peer || peer.destroyed) {
        console.warn(`Ignorando sinal, peer ${roomConnectedInSocketId} não existe ou já foi destruído.`);
        return;
    }

    peer.signal(signal);
    // peers[data.roomConnectedInSocketId].signal(data.signal);
};

/////////////////////////// UI VIDEOS ////////////////////////////////
const showLocalVideoPreview = (stream, identity) => {
    const videosContainer = document.getElementById("id_video_portal");

    if (!videosContainer) {
        return;
    }

    videosContainer.classList.add("video_portal_container");

    const videoPreview = document.createElement("div");
    videoPreview.classList.add("video_track_container");

    const videoTrack = document.createElement("video");

    cameraController(videoTrack, stream, true);
    previewFullScreen(videoTrack, videoPreview);

    videoPreview.appendChild(videoTrack);
    videoPreview.appendChild(getIdentityOverlay(identity));
    videosContainer.appendChild(videoPreview);
};

const addStream = (stream, roomConnectedInSocketId) => {
    const partticipants = store.getState().participants || [];

    partticipants.filter((participant, index, self) => index === self.findIndex((p) => (
        p.roomSocketId === roomConnectedInSocketId && p.identity === participant.identity && p.isRoomListener === participant.isRoomListener
    ))).find((participant) => {
        if (participant.isRoomListener) {
            return;
        }

        const videosContainer = document.getElementById("id_video_portal");

        if (!videosContainer) {
            return;
        }

        videosContainer.classList.add("video_portal_container");

        const videoPreview = document.createElement("div");
        videoPreview.classList.add("video_track_container");
        videoPreview.id = roomConnectedInSocketId;

        const videoTrack = document.createElement("video");
        videoTrack.id = `vd_${roomConnectedInSocketId}`;

        cameraController(videoTrack, stream, false);
        previewFullScreen(videoTrack, videoPreview);

        videoPreview.appendChild(videoTrack);
        videoPreview.appendChild(getIdentityOverlay(participant.identity));
        videosContainer.appendChild(videoPreview);
    });
};

const getIdentityOverlay = (identity = "") => {
    const textContainer = document.createElement("div");
    const textElement = document.createElement("p");

    textContainer.classList.add("paragraph_video_container");
    textElement.classList.add("paragraph_video_element");
    textElement.innerHTML = `${identity}`;

    textContainer.appendChild(textElement);

    return textContainer;
};

const cameraController = (videoTrack, stream, isLocal = false) => {
    videoTrack.srcObject = stream;
    videoTrack.autoplay = true;
    videoTrack.muted = isLocal;
    videoTrack.setAttribute("playsInline", true);

    videoTrack.onloadedmetadata = () => {
        videoTrack.play();
    };
};

const previewFullScreen = (videoTrack, videoPreview) => {
    videoTrack.addEventListener("click", () => {
        if (videoPreview.classList.contains("video_track_container")) {
            videoPreview.classList.remove("video_track_container");
            videoPreview.classList.add("video_portal_full_screen");
        } else {
            videoPreview.classList.remove("video_portal_full_screen");
            videoPreview.classList.add("video_track_container");
        }
    });
};

export const removeSessionPeerConnection = (data) => {
    const { roomSocketId } = data;

    if (!roomSocketId) {
        return;
    }

    if (peers[roomSocketId]) {
        const peer = peers[roomSocketId];

        if (peer.connected) {
            peer.destroy();
        }

        delete peers[roomSocketId];
    }
};

export const removeRoomPeerConnection = (data) => {
    const { roomSocketId } = data;

    if (!roomSocketId) {
        return;
    }

    const videoPreview = document.getElementById(roomSocketId);
    const videoTrack = document.getElementById(`vd_${roomSocketId}`);

    if (videoTrack && videoTrack.srcObject) {
        const tracks = videoTrack.srcObject.getTracks();
        tracks.forEach((track) => track.stop());
        videoTrack.srcObject = null;
    }

    if (videoPreview && videoTrack) {
        videoPreview.removeChild(videoTrack);

        if (videoPreview.parentNode) {
            videoPreview.parentNode.removeChild(videoPreview);
        }
    }

    if (peers[roomSocketId]) {
        const peer = peers[roomSocketId];

        if (peer.connected) {
            peer.destroy();
        }

        delete peers[roomSocketId];
    }
};

export const cleanLocalStreamPeerConnection = () => {
    if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
        localStream = null;
    }
};

export const toggleAudioConnect = (isOnlyAudio) => {
    localStream.getAudioTracks()[0].enabled = isAudioController(isOnlyAudio);
};

export const toggleCameraConnect = (isOnlyCamera) => {
    localStream.getVideoTracks()[0].enabled = isCameraController(isOnlyCamera);
};

const isAudioController = (isOnlyAudio) => {
    return !!isOnlyAudio;
};

const isCameraController = (isOnlyCamera) => {
    return !!isOnlyCamera;
};

export const toggleSharingScreen = (isScreenSharing, screenSharingStream = null) => {
    const stream = isScreenSharing ? localStream : screenSharingStream ?? localStream;

    if (!stream || !stream.getTracks) {
        return;
    }

    switchVideoTracks(stream);
};

export const switchVideoTracks = (stream) => {
    if (!stream || !stream.getTracks) {
        return;
    }

    for (let roomSocketId in peers) {
        const peer = peers[roomSocketId];

        if (!peer || !peer.streams || !peer.streams[0]) {
            continue;
        }

        const peerTracks = peer.streams[0].getTracks();
        const newTracks = stream.getTracks();

        for (let trackA of peerTracks) {
            for (let trackB of newTracks) {
                if (trackA.kind === trackB.kind) {
                    peer.replaceTrack(trackA, trackB, peer.streams[0]);
                    break;
                }
            }
        }
    }
};

/////////////////////////// DISCONNECT SERVER ////////////////////////////////
const serverDisconnect = (authTokenData) => {
    const { authToken } = authTokenData;
    store.dispatch(setAuthToken(authToken));
};

export const sendServerDisconnectDataChannel = (authToken, username) => {
    const localData = {
        [AUTH_TOKEN_KEY]: authToken,
        [USERNAME_KEY]: username
    };

    serverDisconnect(localData);

    const toStringData = JSON.stringify(localData);

    for (let roomSocketId in peers) {
        const peer = peers[roomSocketId];

        if (peer && peer.connected) {
            peer.send(toStringData);
        } else {
            console.warn(`Peer with socketId ${roomSocketId} is not connected.`);
        }
    }
};

/////////////////////////// CHAT MESSAGES ////////////////////////////////
const appendMessage = (messageData) => {
    const messages = store.getState().messages;
    store.dispatch(setMessages([...messages, messageData]));
};

export const sendMessageDataChannel = (username, content) => {
    const localData = {
        [USERNAME_KEY]: username,
        [CONTENT_KEY]: content,
        [CREATE_BY_ME_KEY]: true,
    };

    appendMessage(localData);

    const globalData = {
        [USERNAME_KEY]: username,
        [CONTENT_KEY]: content,
        [CREATE_BY_ME_KEY]: false,
    };

    const toStringData = JSON.stringify(globalData);

    for (let roomSocketId in peers) {
        const peer = peers[roomSocketId];

        if (peer && peer.connected) {
            peer.send(toStringData);
        } else {
            console.warn(`Peer with socketId ${roomSocketId} is not connected.`);
        }
    }
};

/////////////////////////// NOTIFY MESSAGE ////////////////////////////////
const appendNotifyMessage = (notifyData) => {
    store.dispatch(setNotifyMessage([notifyData]));
};

export const sendNotifyMessageDataChannel = async (socketId, avaluator, actor) => {
    const data = {
        [NOTIFY_SOCKET_ID]: socketId,
        [NOTIFY_AVALUATOR]: avaluator,
        [NOTIFY_ACTOR]: actor
    };

    appendNotifyMessage(data);

    const toStringData = JSON.stringify(data);

    for (let roomSocketId in peers) {
        const peer = peers[roomSocketId];

        if (peer && peer.connected) {
            peer.send(toStringData);
        } else {
            console.warn(`Peer with socketId ${roomSocketId} is not connected.`);
        }
    }
};

/////////////////////////// SEND VOLUME ICON ////////////////////////////////
const appendVolumeIconStream = (volumeIconStreamData) => {
    const volumeIconStream = store.getState().volumeIconStream;
    store.dispatch(setVolumeIconStream([...volumeIconStream, volumeIconStreamData]));
};

export const sendVolumeIconStreamDataChannel = (emitterSocketId, isOnlyAudio, roomId) => {
    const data = {
        [ROOM_SOCKET_ID]: emitterSocketId,
        [IS_ONLY_AUDIO]: isOnlyAudio,
        [ROOM_ID]: roomId
    };

    appendVolumeIconStream(data);

    const toStringData = JSON.stringify(data);

    for (let roomSocketId in peers) {
        const peer = peers[roomSocketId];

        if (peer && peer.connected) {
            peer.send(toStringData);
        } else {
            console.warn(`Peer with socketId ${roomSocketId} is not connected.`);
        }
    }

    if (isOnlyAudio) {
        startVolumeDetection(localStream, emitterSocketId);
    } else {
        stopVolumeDetection(emitterSocketId);
    }
};

const startVolumeDetection = (stream, roomSocketId) => {
    if (!audioContext || audioContext.state === "closed") {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }

    if (!stream) {
        return;
    }

    try {
        const source = audioContext.createMediaStreamSource(stream);
        const microGainNode = audioContext.createGain();
        analyser = audioContext.createAnalyser();

        analyser.smoothingTimeConstant = 0.8;
        analyser.fftSize = 1024;

        source.connect(microGainNode);
        microGainNode.connect(analyser);

        monitorVolume(roomSocketId);
    } catch (error) {
        console.error("Erro ao conectar stream ao AudioContext:", error);
    }
};

const monitorVolume = (roomSocketId) => {
    const volumeIconContainer = document.getElementById("volume_icon_portal");

    if (!volumeIconContainer) return;

    let volumeStyle = document.getElementById(roomSocketId);

    if (!volumeStyle) {
        volumeStyle = document.createElement("div");
        volumeStyle.classList.add("btn_volume_circle");
        volumeStyle.id = roomSocketId;

        const volumeIcon = document.createElement("span");
        volumeIcon.classList.add("material-symbols-outlined", "btn_icon_style");
        volumeIcon.id = `volume_icon_${roomSocketId}`;
        volumeIcon.textContent = "volume_up";

        volumeStyle.appendChild(volumeIcon);
        volumeIconContainer.appendChild(volumeStyle);
    }

    const volumeIcon = document.getElementById(`volume_icon_${roomSocketId}`);

    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
    }

    const checkVolume = () => {
        if (!analyser) return;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteTimeDomainData(dataArray);

        const sum = dataArray.reduce((acc, val) => acc + val, 0);
        const average = sum / bufferLength;

        if (average > volumeThreshold) {
            volumeIcon.classList.add("bouncing");
            volumeIcon.textContent = "volume_up";
        } else {
            volumeIcon.classList.remove("bouncing");
            volumeIcon.textContent = "volume_off";
        }

        animationFrameId = requestAnimationFrame(checkVolume);
    };

    animationFrameId = requestAnimationFrame(checkVolume);
};

const stopVolumeDetection = (roomSocketId) => {
    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
    }

    const volumeIcon = document.getElementById(`volume_icon_${roomSocketId}`);

    if (volumeIcon) {
        volumeIcon.classList.remove("bouncing");
        volumeIcon.textContent = "volume_off";
    }

    if (audioContext) {
        audioContext.close().then(() => {
            audioContext = null;
            analyser = null;
        });
    }
};

/////////////////////////// TIME STAMP ////////////////////////////////
const appendTimeStamp = (timeStampData) => {
    const { minutes, seconds } = timeStampData;
    store.dispatch(setInitialMinutes(minutes));
    store.dispatch(setInitialSeconds(seconds));
};

export const sendTimeStampDataChannel = (minutes, seconds, roomId) => {
    const data = {
        [MINUTES_KEY]: minutes,
        [SECONDS_KEY]: seconds,
        [ROOM_ID]: roomId
    };

    appendTimeStamp(data);

    const toStringTimeStampData = JSON.stringify(data);

    for (let roomSocketId in peers) {
        const peer = peers[roomSocketId];

        if (peer && peer.connected) {
            peer.send(toStringTimeStampData);
        } else {
            console.warn(`Peer with socketId ${roomSocketId} is not connected.`);
        }
    }
};

/////////////////////////// TIME ALERT ////////////////////////////////
const appendTimeAlert = (timeAlertData) => {
    const { isTimeStamp, audioSrc } = timeAlertData;
    store.dispatch(setIsTimeStamp(isTimeStamp));
    store.dispatch(setAudioSrc(audioSrc));
};

export const sendTimeAlertDataChannel = (isTimeStamp, audioSrc, roomId) => {
    const data = {
        [IS_TIME_STAMP]: isTimeStamp,
        [AUDIO_SRC]: audioSrc,
        [ROOM_ID]: roomId
    };

    appendTimeAlert(data);

    const toStringTimeAlertData = JSON.stringify(data);

    for (let roomSocketId in peers) {
        const peer = peers[roomSocketId];

        if (peer && peer.connected) {
            peer.send(toStringTimeAlertData);
        } else {
            console.warn(`Peer with socketId ${roomSocketId} is not connected.`);
        }
    }

    createAudioTrack(isTimeStamp, audioSrc);
};

const createAudioTrack = (isTimeStamp, audioSrc) => {
    const audioAlertContainer = document.getElementById("id_timer_audio_alert");
    audioAlertContainer.classList.add("div_timer_audio_alert");
    audioAlertContainer.hidden = true;


    let audioTrack = document.getElementById("id_audio_track_element");

    if (!audioTrack) {
        audioTrack = document.createElement("audio");
        audioTrack.id = "id_audio_track_element";
        audioTrack.type = "audio/mpeg";
        audioAlertContainer.appendChild(audioTrack);
    }

    // Atualiza o src apenas se for diferente
    if (audioTrack.src !== audioSrc) {
        audioTrack.src = audioSrc;
    }

    if (isTimeStamp) {
        audioTrack.play();
    } else {
        audioTrack.pause();
        audioTrack.currentTime = 0;
    }
};

/////////////////////////// SCENARIOS and TASKS ////////////////////////////////
const appendTask = (taskData) => {
    store.dispatch(setTasks(taskData));
};

export const sendTaskDataChannel = (id, areaId, estacaoId, cenario, descricao, tarefas, ativo, pdf, video, orientacoes, localAtuacao, areaName, roomId) => {
    const data = {
        [TASK_ID]: id,
        [AREA_ID]: areaId,
        [TASK_ESTATION_ID]: estacaoId,
        [TASK_SCENARIO]: cenario,
        [AREA_NAME]: areaName,
        descricao: descricao,
        tarefas: tarefas,
        ativo: ativo,
        pdf: pdf,
        video: video,
        orientacoes: orientacoes,
        localAtuacao: localAtuacao,
        [ROOM_ID]: roomId
    };

    appendTask(data);

    const toStringData = JSON.stringify(data);

    for (let roomSocketId in peers) {
        const peer = peers[roomSocketId];

        if (peer && peer.connected) {
            peer.send(toStringData);
        } else {
            console.warn(`Peer with socketId ${roomSocketId} is not connected.`);
        }
    }
};

/////////////////////////// SEND QUESTIONS ////////////////////////////////
const appendSendQuestions = (questionData) => {
    const sendQuestions = store.getState().sendQuestions;
    store.dispatch(setSendQuestions([...sendQuestions, questionData]));
};

export const sendQuestionDataChannel = (id, cardId, perguntaRealizada, resposta, palavrasChaves,
    resumo, imagem, nota_A, nota_PA, nota_I, tipo, dica, ativo, ordem, roomId) => {
    const data = {
        id: id,
        cardId: cardId,
        [QUESTION_REALIZED]: perguntaRealizada,
        resposta: resposta,
        palavrasChaves: palavrasChaves,
        resumo: resumo,
        imagem: imagem,
        [NOTE_A]: nota_A,
        [NOTE_PA]: nota_PA,
        [NOTE_I]: nota_I,
        tipo: tipo,
        dica: dica,
        ativo: ativo,
        ordem: ordem,
        [ROOM_ID]: roomId
    };

    appendSendQuestions(data);

    const toStringData = JSON.stringify(data);

    for (let roomSocketId in peers) {
        const peer = peers[roomSocketId];

        if (peer && peer.connected) {
            peer.send(toStringData);
        } else {
            console.warn(`Peer with socketId ${roomSocketId} is not connected.`);
        }
    }
};

/////////////////////////// SEND PROGRESS NOTES ////////////////////////////////
const appendProgressNote = (progressData) => {
    const dataNotes = store.getState().dataNotes;
    store.dispatch(setDataNotes([...dataNotes, progressData]));
};

const appendSendStateDataNotes = (stateNotes) => {
    const stateDataNotes = store.getState().stateDataNotes;
    store.dispatch(setStateDataNotes([...stateDataNotes, stateNotes]));
};

export const sendProgressNoteDataChannel = (question, note, noteType, roomId) => {
    const noteValue = note * 10;
    const progressValue = handlerProgressWidth(note, "note_result_container");

    let existeNote = store.getState().dataNotes.find(n => n.id === question._id);

    let action;

    if (existeNote && existeNote.noteType === noteType) {
        action = "remove";
    } else {
        action = "add";
    }

    const data = {
        action,
        [PROGRESS_NOTE_ID]: question._id,
        [PROGRESS_NOTE_CARD_ID]: question.cardId,
        [PROGRESS_NOTE_VALUE]: noteValue,
        [PROGRESS_NOTE_SIZE]: progressValue,
        [ROOM_ID]: roomId,
    };

    const sendData = {
        action,
        id: question._id,
        cardId: question.cardId,
        perguntaRealizada: question.perguntaRealizada,
        note: noteValue,
        noteType,
    };

    if (action === "add") {
        store.dispatch(setRemoveDataNotes(question._id));
        store.dispatch(setRemoveStateDataNotes(question._id));

        appendProgressNote(data);
        appendSendStateDataNotes(sendData);
    }

    if (action === "remove") {
        store.dispatch(setRemoveDataNotes(question._id));
        store.dispatch(setRemoveStateDataNotes(question._id));
    }

    const toStringData = JSON.stringify(data);

    for (let roomSocketId in peers) {
        const peer = peers[roomSocketId];

        if (peer && peer.connected) {
            peer.send(toStringData);
        } else {
            console.warn(`Peer with socketId ${roomSocketId} is not connected.`);
        }
    }
};

// export const sendProgressNoteDataChannel = (question, note, noteType, roomId) => {
//     const noteValue = note * 10;
//     const progressValue = handlerProgressWidth(note, "note_result_container");

//     let existeNote = store.getState().dataNotes.find(n => n.id === question._id);

//     // const action = existeNote ? "remove" : "add";
//     let action = "add";
//     if (existeNote) action = "update";

//     const data = {
//         action,
//         [PROGRESS_NOTE_ID]: question._id,
//         [PROGRESS_NOTE_CARD_ID]: question.cardId,
//         [PROGRESS_NOTE_VALUE]: noteValue,
//         [PROGRESS_NOTE_SIZE]: progressValue,
//         [ROOM_ID]: roomId
//     };

//     const sendData = {
//         action,
//         id: question._id,
//         cardId: question.cardId,
//         perguntaRealizada: question.perguntaRealizada,
//         note: noteValue,
//         noteType: noteType
//     };

//     if (action === "add" || action === "update") {
//         appendProgressNote(data);
//         appendSendStateDataNotes(sendData);
//     }

//     const toStringData = JSON.stringify(data);

//     for (let roomSocketId in peers) {
//         const peer = peers[roomSocketId];

//         if (peer && peer.connected) {
//             peer.send(toStringData);
//         } else {
//             console.warn(`Peer with socketId ${roomSocketId} is not connected.`);
//         }
//     }

//     if (action === "remove") {
//         store.dispatch(setRemoveDataNotes(question._id));
//         store.dispatch(setRemoveStateDataNotes(question._id));
//     }
// };

const handlerProgressWidth = (note, elementId = "note_result_container") => {
    const width = getWidthComponent(elementId);

    if (width <= 0) {
        return 0;
    }

    return (note / 10) * width;
};

/////////////////////////// SEND NOTE ////////////////////////////////
const appendSendNotes = (questionData) => {
    const sendDataNotes = store.getState().sendDataNotes;
    store.dispatch(setSendDataNotes([...sendDataNotes, questionData]));
};

export const sendNoteDataChannel = (id, perguntaRealizada, noteValue, noteType, roomId) => {
    const data = {
        [SEND_NOTE_ID]: id,
        [SEND_NOTE_PARAGRAPH]: perguntaRealizada,
        [SEND_NOTE_VALUE]: noteValue,
        [SEND_NOTE_TYPE]: noteType,
        [ROOM_ID]: roomId
    };

    appendSendNotes(data);

    const toStringData = JSON.stringify(data);

    for (let roomSocketId in peers) {
        const peer = peers[roomSocketId];

        if (peer && peer.connected) {
            peer.send(toStringData);
        } else {
            console.warn(`Peer with socketId ${roomSocketId} is not connected.`);
        }
    }
};